import React from 'react';

/**
 * Lightweight, open-source-safe illustration for any exercise. Instead of
 * pulling arbitrary photos we render a small stylised SVG per muscle group
 * so users get a visual cue at a glance without shipping licensed assets.
 * Two visual layers:
 *   1. Silhouette body outline (front)
 *   2. Highlighted muscle group tinted in userRed
 */
const MG_HIGHLIGHTS = {
  Chest: (
    <>
      <path d="M22 33 Q32 30 42 33 Q41 41 32 43 Q23 41 22 33Z" fill="currentColor" opacity="0.9" />
    </>
  ),
  Shoulders: (
    <>
      <circle cx="19" cy="32" r="5" fill="currentColor" opacity="0.9" />
      <circle cx="45" cy="32" r="5" fill="currentColor" opacity="0.9" />
    </>
  ),
  Back: (
    <>
      <path d="M23 33 L41 33 L44 55 L20 55 Z" fill="currentColor" opacity="0.7" />
      <path d="M31 33 L33 33 L33 55 L31 55 Z" fill="#EDEAE3" opacity="0.4" />
    </>
  ),
  Biceps: (
    <>
      <ellipse cx="17" cy="41" rx="4" ry="6" fill="currentColor" opacity="0.9" />
      <ellipse cx="47" cy="41" rx="4" ry="6" fill="currentColor" opacity="0.9" />
    </>
  ),
  Triceps: (
    <>
      <path d="M13 40 Q17 38 20 40 L20 48 Q17 50 14 48 Z" fill="currentColor" opacity="0.9" />
      <path d="M44 40 Q47 38 51 40 L50 48 Q47 50 44 48 Z" fill="currentColor" opacity="0.9" />
    </>
  ),
  Forearms: (
    <>
      <path d="M12 50 Q15 49 18 50 L17 60 Q15 61 13 60 Z" fill="currentColor" opacity="0.9" />
      <path d="M46 50 Q49 49 52 50 L51 60 Q49 61 47 60 Z" fill="currentColor" opacity="0.9" />
    </>
  ),
  Core: (
    <>
      <rect x="27" y="45" width="10" height="14" rx="2" fill="currentColor" opacity="0.85" />
      <line x1="32" y1="46" x2="32" y2="58" stroke="#EDEAE3" strokeWidth="0.6" />
      <line x1="27" y1="50" x2="37" y2="50" stroke="#EDEAE3" strokeWidth="0.6" />
      <line x1="27" y1="54" x2="37" y2="54" stroke="#EDEAE3" strokeWidth="0.6" />
    </>
  ),
  Legs: (
    <>
      <path d="M26 60 L30 60 L31 88 L27 88 Z" fill="currentColor" opacity="0.9" />
      <path d="M34 60 L38 60 L37 88 L33 88 Z" fill="currentColor" opacity="0.9" />
    </>
  ),
  Glutes: (
    <>
      <ellipse cx="28" cy="60" rx="6" ry="4" fill="currentColor" opacity="0.9" />
      <ellipse cx="36" cy="60" rx="6" ry="4" fill="currentColor" opacity="0.9" />
    </>
  ),
  Hamstrings: (
    <>
      <path d="M26 62 L30 62 L31 78 L27 78 Z" fill="currentColor" opacity="0.9" />
      <path d="M34 62 L38 62 L37 78 L33 78 Z" fill="currentColor" opacity="0.9" />
    </>
  ),
  Calves: (
    <>
      <ellipse cx="28" cy="86" rx="3" ry="5" fill="currentColor" opacity="0.9" />
      <ellipse cx="36" cy="86" rx="3" ry="5" fill="currentColor" opacity="0.9" />
    </>
  ),
  Cardio: (
    <g>
      <path
        d="M32 18 C24 22 20 34 32 44 C44 34 40 22 32 18 Z"
        fill="currentColor"
        opacity="0.9"
      />
      <path d="M32 46 L32 60" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.7" />
    </g>
  ),
};

const SILHOUETTE = (
  <g>
    {/* Head */}
    <ellipse cx="32" cy="14" rx="6" ry="7" fill="#191C21" opacity="0.09" />
    {/* Neck */}
    <rect x="30" y="20" width="4" height="4" fill="#191C21" opacity="0.09" />
    {/* Torso */}
    <path
      d="M20 24 Q32 22 44 24 L46 60 Q32 63 18 60 Z"
      fill="#191C21"
      opacity="0.09"
    />
    {/* Arms */}
    <path d="M20 26 L14 60 L18 61 L22 30 Z" fill="#191C21" opacity="0.09" />
    <path d="M44 26 L50 60 L46 61 L42 30 Z" fill="#191C21" opacity="0.09" />
    {/* Legs */}
    <path d="M22 60 L26 92 L31 92 L31 60 Z" fill="#191C21" opacity="0.09" />
    <path d="M33 60 L33 92 L38 92 L42 60 Z" fill="#191C21" opacity="0.09" />
  </g>
);

export default function MuscleIllustration({ muscleGroup, size = 64, className = '', testId }) {
  const key = MG_HIGHLIGHTS[muscleGroup] ? muscleGroup : 'Cardio';
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 96"
      className={`text-userRed ${className}`}
      data-testid={testId || `muscle-illustration-${key}`}
      aria-label={`${muscleGroup || 'Muscle'} illustration`}
    >
      {SILHOUETTE}
      {MG_HIGHLIGHTS[key]}
    </svg>
  );
}
